import React from "react";
import {
  RecBar,
  RecField,
  RecIcon,
  useKeepFocusedFieldVisible,
  useKeyboardOpen,
} from "../materials/MaterialRecordParts";
import { BarMenu } from "./TimeLogBarMenu";
import { TimeLogMaterialsSection } from "../TimeLogMaterialsSection";
import { formatCompactDuration } from "../../helpers/dateTimeHelpers";
import { formatCurrency } from "../../helpers/estimateTotals";
import { payrollPill, titleCase } from "./timeLogModel";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { DeskCol, DeskGrid } from "../ops/DeskCols";
import "../ops/opsDesktop.css";
import "../materials/materialRecord.css";
import "./timeLogs.css";

function Select({ id, value, onChange, options, disabled = false }) {
  return (
    <span className="rec-select">
      <select
        id={id}
        className="rec-input"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </span>
  );
}

/** Calculated / owned values read as information, not as a control to type in. */
function Static({ children, muted = false }) {
  return <div className={`tl-static${muted ? " is-muted" : ""}`}>{children}</div>;
}

/**
 * EDIT / CREATE MODE for one Time Log, in the same record-editor language as
 * Materials. Presentation only: the parent owns every value, the calculation,
 * validation and persistence. All fields of the previous form are kept.
 */
export function TimeLogEditor({
  mode,
  saving,
  actionLoading,
  values: v,
  options: o,
  on,
  selectedLog,
  canArchive,
  canDelete,
}) {
  const isDesktop = useIsDesktopViewport();
  useKeyboardOpen(true);
  useKeepFocusedFieldVisible(true);
  const editing = mode === "edit";
  const busy = saving || actionLoading;
  const net = v.netHoursPreview;
  const pill = selectedLog ? payrollPill(selectedLog) : null;
  const barItems = !editing
    ? []
    : [
        ...(canArchive
          ? [{ key: "archive", label: "Archive time log", danger: true, onSelect: on.archive }]
          : []),
        ...(canDelete
          ? [{ key: "delete", label: "Delete time log", danger: true, onSelect: on.requestDelete }]
          : []),
      ];

  const notesSection = (
    <section className="rec-card">
          <RecField label="Notes" htmlFor="tl-notes" icon="doc">
            <textarea
              id="tl-notes"
              className="rec-input rec-input--area"
              value={v.notes}
              onChange={(e) => on.notes(e.target.value)}
              placeholder="Optional notes"
              rows={3}
            />
          </RecField>
        </section>
  );

  return (
    <div className={`screen nx-screen rec-page rec-page--edit${isDesktop ? " tl-desk" : ""}`} data-testid="time-log-editor">
      <RecBar
        title={editing ? "Edit Time Log" : "New Time Log"}
        leading="close"
        onBack={on.cancel}
        trailing={<BarMenu items={barItems} />}
      />

      <div className="rec-body">
        <DeskGrid on={isDesktop} className="tl-desk-grid">
        <DeskCol on={isDesktop}>
        <section className="rec-card">
          <RecField label="Project" htmlFor="tl-project" icon="folder">
            <Select
              id="tl-project"
              value={v.projectId}
              onChange={on.projectId}
              options={[
                { value: "", label: "None Selected" },
                ...o.projects.map((p) => ({ value: p.project_id, label: p.project_name })),
              ]}
            />
          </RecField>
        </section>

        <section className="rec-card tl-section" aria-label="Date and time">
          <div className="rec-label">
            <RecIcon name="calendar" />
            Date &amp; Time
          </div>
          <div className="tl-grid2">
            <RecField label="Start Date" htmlFor="tl-start-date">
              <input
                id="tl-start-date"
                type="date"
                className="rec-input tl-dt"
                value={v.startDateIso}
                onChange={(e) => on.startDateIso(e.target.value)}
              />
            </RecField>
            <RecField label="Start Time" htmlFor="tl-start-time">
              <input
                id="tl-start-time"
                type="time"
                className="rec-input tl-dt"
                value={v.startTime}
                onChange={(e) => on.startTime(e.target.value)}
              />
            </RecField>
            <RecField label="End Date" htmlFor="tl-end-date">
              <input
                id="tl-end-date"
                type="date"
                className="rec-input tl-dt"
                value={v.endDateIso}
                onChange={(e) => on.endDateIso(e.target.value)}
              />
            </RecField>
            <RecField label="End Time" htmlFor="tl-end-time">
              <input
                id="tl-end-time"
                type="time"
                className="rec-input tl-dt"
                value={v.endTime}
                onChange={(e) => on.endTime(e.target.value)}
              />
            </RecField>
          </div>
          <div className="tl-grid2 tl-grid2--tight">
            <RecField label="Break (minutes)" htmlFor="tl-break">
              <input
                id="tl-break"
                type="number"
                min="0"
                inputMode="numeric"
                className="rec-input"
                value={v.breakMinutes}
                onChange={(e) => on.breakMinutes(e.target.value)}
              />
            </RecField>
            <div className="tl-duration" data-testid="time-log-net">
              <span className="tl-duration__icon">
                <RecIcon name="clock" />
              </span>
              <span className="tl-duration__body">
                <span className="tl-duration__label">Net Duration</span>
                <span className="tl-duration__value">
                  {net == null ? "—" : formatCompactDuration(net)}
                </span>
                <span className="tl-duration__sub">
                  {net == null ? "" : `${net.toFixed(2)} hrs`}
                </span>
              </span>
            </div>
          </div>
        </section>

        <section className="rec-card tl-section">
          <RecField label="Employee" htmlFor="tl-employee" icon="user">
            {editing || !o.canAssignEmployee ? (
              <Static>{v.employeeName || "—"}</Static>
            ) : (
              <Select
                id="tl-employee"
                value={v.employeeId}
                onChange={on.employeeId}
                options={o.employeeOptions}
              />
            )}
          </RecField>
          <RecField label="Work Type" htmlFor="tl-category" icon="wrench">
            <Select
              id="tl-category"
              value={v.workCategory}
              onChange={on.workCategory}
              options={o.categoryOptions}
            />
          </RecField>
          <RecField label="Work Completed" htmlFor="tl-work">
            <input
              id="tl-work"
              className="rec-input"
              value={v.workCompleted}
              onChange={(e) => on.workCompleted(e.target.value)}
              placeholder="Brief description"
              autoComplete="off"
            />
          </RecField>
        </section>


        {isDesktop ? notesSection : null}
        </DeskCol>
        <DeskCol on={isDesktop}>
        <section className="rec-card tl-section" aria-label="Payroll">
          <div className="rec-label">
            <RecIcon name="dollar" />
            Payroll
          </div>
          <div className="tl-grid2">
            <RecField label="Payroll Type" htmlFor="tl-payroll">
              <Select
                id="tl-payroll"
                value={v.payrollType}
                onChange={on.payrollType}
                options={o.payrollOptions}
              />
            </RecField>
            <RecField label="Hourly Rate" htmlFor="tl-rate">
              <Static>
                {v.rateResolving ? "Resolving…" : `${formatCurrency(v.hourlyRate || 0)} / hr`}
              </Static>
            </RecField>
            <RecField label="Trade" htmlFor="tl-trade">
              <Static muted>{v.tradeName || "—"}</Static>
            </RecField>
            <RecField label="Classification" htmlFor="tl-class">
              <Static muted>{v.classificationName || "—"}</Static>
            </RecField>
          </div>
          {editing && selectedLog ? (
            <div className="tl-status">
              <span className="rec-pill">{titleCase(selectedLog.approval_status || "pending")}</span>
              <span className={`rec-pill tl-pill tl-pill--${pill.tone}`}>{pill.label}</span>
            </div>
          ) : null}
        </section>


        {isDesktop ? null : notesSection}

        {editing && selectedLog?.id ? (
          <section className="rec-card tl-materials">
            <TimeLogMaterialsSection timeLog={selectedLog} />
          </section>
        ) : (
          <section className="rec-card tl-materials-hint">
            <div className="rec-label">
              <RecIcon name="cube" />
              Materials Used
            </div>
            <div className="sw-materials-compact-empty">
              Save this time log first, then add materials used during the work session.
            </div>
          </section>
        )}
        </DeskCol>
        </DeskGrid>
      </div>

      <div className="rec-actions rec-actions--split">
        <button type="button" className="rec-btn rec-btn--ghost" onClick={on.cancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--primary" onClick={on.save} disabled={busy}>
          {saving ? "Saving…" : editing ? "Update Time Log" : "Create Time Log"}
        </button>
      </div>
    </div>
  );
}
